import React, { FC, useState } from 'react';
import PortalHeader from '../../assessment-portal/components/portal-header';
import {
  MOBILE_PRIMARY_CTA_CLASS,
  MOBILE_PRIMARY_CTA_ENABLED_CLASS,
} from '../../assessment-portal/components/primary-cta';

interface WelcomePageProps {
  onNext: () => void
}

const todayParts = [
  {
    num: 1,
    title: 'A bit about you',
    subtitle: 'Your details, history & consent',
  },
  {
    num: 2,
    title: 'Pick your appointment',
    subtitle: 'See the fee, choose a time',
  },
  {
    num: 3,
    title: 'Questionnaire',
    subtitle: '~45 min, at your own pace — pause anytime',
  },
] as const;

const careJourneySteps = [
  {
    num: '01', label: 'Booking & questionnaire', status: "You're here", isHere: true,
  },
  {
    num: '02', label: 'Telehealth intake', status: '30–40 min', isHere: false,
  },
  {
    num: '03', label: 'Psychiatry appointment', status: '45–60 min', isHere: false,
  },
  {
    num: '04', label: 'Therapy & support', status: 'ongoing', isHere: false,
  },
] as const;

const WelcomePage: FC<WelcomePageProps> = ({ onNext }) => {
  const [showJourney, setShowJourney] = useState(false);

  return (
    <div className="adhd-portal adhd-welcome-page min-h-screen flex flex-col bg-page">
      <PortalHeader
        title="Welcome"
        subtitle="ADHD assessment"
        chip="Before you start"
        chipTone="grey"
        progress={0}
      />
      <main className="flex-1 flex items-start justify-center px-4 py-6 md:py-8 md:pb-8">
        <div className="adhd-welcome-shell">
          <div className="adhd-welcome-hero">
            <img
              src={`${process.env.PUBLIC_URL}/favicon.svg`}
              alt="Telecare"
              className="adhd-welcome-logo block mx-auto mb-[18px] h-10 w-10"
            />
            <h1 className="text-[28px] font-extrabold leading-[1.16] mb-[14px] text-[#171D4A] tracking-[-0.5px]">
              Book your ADHD/ASD Intake Appointment
            </h1>
            <p className="text-[15px] text-[#5B6478] leading-[1.55] mb-[26px]">
              Takes about 15 minutes — it&apos;s best to complete it in one sitting.
            </p>
            <button
              type="button"
              onClick={onNext}
              className="adhd-welcome-cta adhd-welcome-cta--hero"
            >
              Start Booking
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
            <p className="adhd-welcome-payment-note">
              No payment needed today
            </p>
          </div>

          <div className="adhd-welcome-steps">
            <p className="adhd-welcome-steps-label">
              <span>What you&apos;ll do today · 3 parts</span>
            </p>
            {todayParts.map((part, index) => (
              <div
                key={part.num}
                className={`adhd-welcome-step-row${index === 0 ? ' is-first' : ''}`}
              >
                <span className="adhd-welcome-step-num">
                  {part.num}
                </span>
                <div>
                  <div className="text-[15px] font-bold text-[#171D4A] leading-[1.3]">{part.title}</div>
                  <div className="text-[14px] text-[#6B7280] leading-[1.45] mt-[3px]">{part.subtitle}</div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={() => setShowJourney((v) => !v)}
              className="adhd-welcome-link min-h-yn-btn"
              aria-expanded={showJourney}
            >
              See your full care journey
              <svg
                viewBox="0 0 24 24"
                width="17"
                height="17"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform ${showJourney ? 'rotate-180' : ''}`}
                aria-hidden
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {showJourney && (
              <div className="adhd-welcome-journey">
                <p className="adhd-welcome-journey-intro">
                  The four stages of your care. Times shown are typical appointment
                  lengths — not how long you&apos;ll wait.
                </p>
                {careJourneySteps.map((step) => (
                  <div key={step.num} className="adhd-welcome-journey-row">
                    <span className={`adhd-welcome-journey-badge${step.isHere ? ' is-here' : ''}`}>
                      {step.num}
                    </span>
                    <span className="adhd-welcome-journey-label">{step.label}</span>
                    <span className={`adhd-welcome-journey-status${step.isHere ? ' is-here' : ''}`}>
                      {step.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      <div className="portal-navrow-spacer md:hidden" aria-hidden />
      <div className="portal-navrow--sticky md:hidden">
        <button
          type="button"
          onClick={onNext}
          className={`${MOBILE_PRIMARY_CTA_CLASS} ${MOBILE_PRIMARY_CTA_ENABLED_CLASS}`}
        >
          Get started
        </button>
      </div>
    </div>
  );
};

export default WelcomePage;
